import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Grid, Button } from "@mui/material";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import { useNavigate } from "react-router-dom";
import { INVOICE_FORMATS, KEYBOARD_KEYS, SALE_STATUS, ROUTES, ESTIMATE_STATUS_LABELS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import { amountRoundOff } from "helpers/NumberFormatHelper";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./styles.module.css";
import ShopContext from "context/ShopContext";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import A4Invoice from "components/utils/PrintInvoice/A4Invoice";
import A5Invoice from "components/utils/PrintInvoice/A5Invoice";
import ThreeInchInvoice from "components/utils/PrintInvoice/ThreeInchInvoice";
import toast from "react-hot-toast";
import * as SalesHelper from "helpers/SalesHelper";
import * as StringHelper from "helpers/StringHelper";
import SalesContext from "context/SalesContext";
import SaleEditDialog from "../SaleEditDialog/SaleEditDialog";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import EditIcon from "images/svg/edit.svg";

export default function EstimateDetailsDialog({ open, onClose, estimateId }) {
  const { selectedShop } = useContext(ShopContext);
  const { getSaleById, setActiveSale } = useContext(SalesContext);

  const navigate = useNavigate();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billDialogOpen, setBillDialogOpen] = useState(false);

  const handleConvertToSale = () => {
    if (sale?.local_id) {
      setBillDialogOpen(true);
    } else {
      toast.error("Estimate does not have a valid local ID.");
    }
  };

  const printComponentRef = useRef(null);

  const fetchEstimateDetails = useCallback(async () => {
    setLoading(true);
    try {
      const saleData = await getSaleById(estimateId);
      if (saleData) {
        setSale(saleData);
      } else {
        setSale(null);
      }
    } catch (error) {
      console.error("Error fetching estimate details:", error);
    } finally {
      setLoading(false);
    }
  }, [getSaleById, estimateId]);

  useEffect(() => {
    if (open && estimateId) {
      fetchEstimateDetails();
    }
  }, [open, estimateId, fetchEstimateDetails]);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: printComponentRef,
    documentTitle: `Estimate-${sale?.estimate_no || "NA"}`,
  });

  const onPrintClick = useCallback(async () => {
    try {
      await SalesHelper.saveAndProcess(sale, {
        shop: selectedShop,
        fallbackPrintFunction: handleWebInvoicePrint,
      });
    } catch (error) {
      toast.error(error?.message || "Failed to print invoice.");
    }
  }, [sale, selectedShop, handleWebInvoicePrint]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open || !sale) return;

      if (event.key === KEYBOARD_KEYS.F4) {
        event.preventDefault();
        onPrintClick();
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [open, sale, onPrintClick]);

  const invoiceFormat = SalesHelper.getInvoicePrinterFormat();

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEditClick = () => {
    if (typeof setActiveSale === "function") {
      setActiveSale(JSON.parse(JSON.stringify(sale)));
    }

    onClose?.();
    navigate(ROUTES.SALES);
  };

  const isConvertedToSale = sale?.status !== SALE_STATUS.PAID;

  return (
    <div className={styles.popUp} onClick={handleBackdropClick}>
      {!billDialogOpen && (
        <Grid container className={styles.billContentWrapper} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <Grid item xs={12} className={styles.headSec} mb={2}>
            <h3>Estimate Details</h3>
            <CloseIcon className={styles.icon} onClick={onClose} />
          </Grid>

          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", width: "100%", color: "#666" }}>
              Loading estimate details...
            </div>
          ) : !sale ? (
            <div style={{ padding: "40px", textAlign: "center", width: "100%", color: "#666" }}>
              Estimate details not found for ID: <strong>{estimateId}</strong>
            </div>
          ) : (
            <>
              {/* Meta info section */}
              <Grid item xs={12} className={styles.headInfoSec}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Estimate Number</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{sale.estimate_no}</div>
                    </div>
                    {sale.customer && (
                      <div className={styles.priceTxt}>
                        <div className={styles.subTitle}>Customer</div>
                        <div className={styles.colon}>:</div>
                        <div className={styles.amountGray}>{sale.customer?.name || "-"}</div>
                      </div>
                    )}
                    {sale.customer?.mobile && (
                      <div className={styles.priceTxt}>
                        <div className={styles.subTitle}>Mobile</div>
                        <div className={styles.colon}>:</div>
                        <div className={styles.amountGray}>{sale.customer?.mobile || "-"}</div>
                      </div>
                    )}
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Items</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{sale.items?.length || 0}</div>
                    </div>
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Date & Time</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>
                        {sale.time ? DateTimeHelpers.formatDateTime(sale.time) : sale.dateTime || "-"}
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={6}>
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Total Payable</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>
                        <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                        {amountRoundOff(sale.payable || sale.total)}
                      </div>
                    </div>

                    <div className={styles.statusContainer}>
                      <div className={styles.statusLabel}>Sync Status</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.statusValue}>{StringHelper.formatLabel(sale.sync_status)}</div>
                    </div>

                    <div className={styles.statusContainer}>
                      <div className={styles.statusLabel}>Sale Status</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.statusValue}>
                        {sale.status === SALE_STATUS.PAID
                          ? ESTIMATE_STATUS_LABELS.CONVERTED
                          : StringHelper.formatLabel(sale.status)}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>

              {/* Items Table */}
              <Grid item xs={12} className={styles.tableContainer}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "10%" }}>No</th>
                      <th style={{ width: "40%" }}>Items</th>
                      <th style={{ width: "10%" }}>Qty</th>
                      <th style={{ width: "40%" }}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sale.items || []).map((item, index) => (
                      <tr key={index}>
                        <td style={{ width: "10%" }}>{index + 1}</td>
                        <td style={{ width: "40%" }}>{item.name || item.item?.name}</td>
                        <td style={{ width: "10%" }}>{item.count ?? item.qty ?? 1}</td>
                        <td style={{ width: "40%" }}>{item.note ?? "-"}</td>
                      </tr>
                    ))}
                    {(!sale.items || sale.items.length === 0) && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                          No items found in this estimate
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Grid>

              <div className={styles.border}></div>

              <Grid item xs={12} className={styles.footSec} mt={2}>
                <Button variant="outlined" className={styles.buttonGray} onClick={onPrintClick}>
                  Re Print
                </Button>
                {isConvertedToSale && (
                  <ButtonGray
                    className={styles.buttonGray}
                    startIcon={<img src={EditIcon} alt="Print" className={styles.iconSmall} />}
                    onClick={handleEditClick}
                  >
                    Edit
                  </ButtonGray>
                )}
                {isConvertedToSale && (
                  <ButtonRed className={styles.footerBtn} onClick={handleConvertToSale}>
                    Convert to Sale
                  </ButtonRed>
                )}
              </Grid>
            </>
          )}
        </Grid>
      )}
      {billDialogOpen && sale?.local_id && (
        <SaleEditDialog
          handleClose={() => setBillDialogOpen(false)}
          onDone={() => {
            setBillDialogOpen(false);
            onClose();
          }}
          saleLocalId={sale?.local_id}
        />
      )}
      <div
        style={{
          position: "absolute",
          left: "-10000px",
          top: "-10000px",
          visibility: "hidden",
        }}
      >
        {invoiceFormat === INVOICE_FORMATS.A4 ? (
          <A4Invoice ref={printComponentRef} sale={sale} shop={selectedShop} />
        ) : invoiceFormat === INVOICE_FORMATS.A5 ? (
          <A5Invoice ref={printComponentRef} sale={sale} shop={selectedShop} />
        ) : (
          <ThreeInchInvoice ref={printComponentRef} sale={sale} shop={selectedShop} />
        )}
      </div>
    </div>
  );
}
